import Grid, { GridItem, GridItemHero, GridItemStill, ObjectFit } from '../components/generic/Grid';
import { Link, graphql } from 'gatsby';
import Section, { size } from '../components/generic/Section';

import Head from '../components/Head';
import { ProductAvailability } from '../constants/contentful';
import ProductFeature from '../components/Products/ProductFeature';
import ProductsToc from '../components/Products/Toc';
import React from 'react';
import getRoute from '../routes/getRoute';
import { isBuildTargetWebex } from '../utils/getBuildTarget';
import styles from './productCategory.module.scss';

const sectionNavTitles = [
  { id: 'overview', title: 'Overview' },
  { id: 'features', title: 'Features' },
  { id: 'products', title: 'Products' },
];

/**
 *
 * @typedef {object} ProductCategory
 * @property {Array<import('../components/Products/ProductFeature').Feature>} sections
 * @property {string} heading
 * @property {{description: string}} description
 * @property {Array<object>} images (from .thumbnails on the ContentfulProductCategory type)
 * @property {string} productGridTitle
 * @property {Array<object>} products
 */
/**
 *
 * @param {object} props
 * @param {string} props.location
 * @param {string} props.path
 * @param {{id: string}} props.pageContext
 * @param {{productCategory: ProductCategory}} props.data
 * @returns
 */
export default function ProductCategory(props) {
  let pageData = props.data.productCategory;
  let location = props.location;
  let hero = pageData.sections?.find(feature => feature.display === 'Hero');
  let features = pageData.sections?.filter(feature => feature.display !== 'Hero');

  return (
    <div className={isBuildTargetWebex() ? styles.webexBackground : undefined}>
      <Head
        location={location}
        title={pageData.heading}
        description={pageData.description.description}
        image={pageData.images?.[0].image?.file?.url}
      />

      <ProductFeature feature={hero} id={sectionNavTitles[0].id} className={styles.hero} />
      {Boolean(features?.length) &&
        features.map((feature, index) => (
          <ProductFeature key={index} feature={feature} id={index === 0 ? sectionNavTitles[1].id : undefined} />
        ))}

      <ProductGrid
        products={
          pageData.products?.filter(
            product =>
              product.__typename === 'ContentfulProductCategory' ||
              product.availability === ProductAvailability.Available ||
              product.availability === ProductAvailability.Released
          ) || []
        }
        productGridTitle={pageData.productGridTitle}
        id={sectionNavTitles[2].id}
      />

      <ProductsToc
        sections={sectionNavTitles.filter(item => {
          // only display 'features' if we got that section
          switch (item.id) {
            case 'features': {
              return Boolean(features && features.length);
            }
            default:
              return true;
          }
        })}
        categoryLink={props.path}
        categoryHeading={pageData.heading} //? add more nesting when pageData.heading (nested project category)
      />
    </div>
  );
}

function ProductGrid({ products, productGridTitle, id }) {
  return (
    <Section as="nav" className={styles.grid} size={size.max} id={id}>
      <Grid>
        <GridItemHero
          heading={productGridTitle || 'Products in category'}
          description={null}
          className={isBuildTargetWebex() ? styles.webex : undefined}
        />
        {products.map(product => {
          const link = getRoute(product);
          return (
            <GridItem key={product.id} gridSize={product.gridSize} className={styles.gridHero}>
              <Link className={styles.link} to={link}>
                <GridItemStill
                  objectFit={ObjectFit.contain}
                  heading={product.shortHeading || product.heading}
                  image={product.images?.[0]?.image}
                  description={product.description}
                  backgroundColor={isBuildTargetWebex() ? '#181818' : product.backgroundColor || '#F2F4F5'}
                  gridSize={product.gridSize}
                  gridLayout={product.gridLayout}
                  highlight={product?.highlight}
                  availability={product.availability}
                />
              </Link>
            </GridItem>
          );
        })}
      </Grid>
    </Section>
  );
}

export const query = graphql`
  query($id: String!) {
    productCategory: contentfulProductCategory(id: { eq: $id }) {
      ...ProductCategory
      products {
        __typename
        ...Product
        ... on ContentfulProduct {
          categories: product_category {
            id
            slug
            ...ParentProductCategory
          }
        }
        ...WorkspaceBundles
        ...ProductCategory
        ...ParentProductCategory
      }
      ...ParentProductCategory
    }
  }
`;

export let productCategoryFragment = graphql`
  fragment WorkspaceBundles on ContentfulProduct {
    bundles: product_bundle {
      workspace {
        ...WorkspaceGridCard
      }
    }
  }

  fragment ProductCategory on ContentfulProductCategory {
    id
    heading
    shortHeading
    description {
      description
    }
    images: thumbnails {
      ...PwpImage
    }
    hideProductComparison
    slug
    gridSize
    gridLayout
    highlight
    backgroundColor
    sections {
      ... on ContentfulDeviceSection {
        id
        __typename
        heading
        shortHeading
        display
        textPosition
        description {
          raw
          references {
            ...RichTextAssetBase
          }
        }
        images {
          ...PwpImage
        }
      }
      ...ParentDevice
    }
    productGridTitle
    products {
      ... on ContentfulProduct {
        ...Product
      }
    }
  }

  fragment ParentProductCategory on ContentfulProductCategory {
    parents: product_category {
      id
      slug
      parents: product_category {
        id
        slug
      }
    }
  }

  fragment ParentDevice on ContentfulDeviceSection {
    mainFrame
    parentDevices: device {
      __typename
      heading
      animationUrl
    }
  }
`;
